// Lazy-forwarding Sentry shim.
//
// Historically this was a pure no-op. Importing `@sentry/nextjs` synchronously
// from a module reachable during build/prerender makes
// `@opentelemetry/sdk-trace-base` fail ("Cannot read properties of undefined
// (reading 'AlwaysOn')"). A 2026-06-17 fix resolved exactly that by lazy-loading
// Sentry out of audio-player-context / swr-config / error-boundary. See TES-1430.
//
// The no-op version silently discarded every `captureException` / `captureMessage`
// across ~59 files, voiding server-side error reporting. This module now FORWARDS
// each call to the real SDK via a runtime dynamic `import()`, keeping the exact
// synchronous, build-safe call signature so no call site has to change.
//
// TES-2249: the forward above is still not enough on its own. `void
// loadSentry().then(...)` is a promise nobody awaits, and Vercel's Node.js
// functions freeze (suspend, not terminate) the instant the response finishes
// streaming — a short request routinely finishes and freezes before that `.then`
// continuation ever gets a turn, so the capture is silently discarded exactly
// like the no-op era, just with extra steps. Proven 2026-09-22: the same probe
// produced a `server-error census` line on every local run and on ZERO of four
// Vercel production attempts.
//
// The fix is `waitUntil`: Vercel injects a request-scoped context object at
// `globalThis[Symbol.for('@vercel/request-context')]` whose `.waitUntil(promise)`
// tells the platform to keep the function alive until `promise` settles, without
// the caller awaiting anything. This is the same primitive `next/server`'s
// `after()` and `@vercel/functions`'s `waitUntil()` are built on. We read the
// global directly instead of importing either package because this module is
// imported by ~30 client components (`hooks/useAudioPlayer.ts`,
// `components/ui/audio-processor.tsx`, ...) — a static `import ... from
// 'next/server'` here would pull a server-only package into those client
// bundles. A `globalThis` symbol lookup has no such cost and is a plain no-op
// (falls back to the old fire-and-forget path) in the browser, in local dev,
// and on any host that doesn't populate the symbol.
//
// Contract:
//  - Fire-and-forget: callers never await; a call returns `void` synchronously.
//  - On Vercel, the forward is additionally registered with `waitUntil` so the
//    platform will not freeze the function before it completes.
//  - No-op during the production build phase (`NEXT_PHASE`) and when no DSN is
//    configured, preserving the prior build/prerender safety.
//  - Any dynamic-import rejection (Sentry outage, missing module) is swallowed so
//    it can never surface as an application error.
//  - The resolved module is cached, so repeated calls do not re-import.
//
// Do NOT add a synchronous top-level `import * as Sentry from '@sentry/nextjs'`
// to this file — that reintroduces the OpenTelemetry build failure. Do NOT
// import `next/server` or `@vercel/functions` here either — see above.
// Do NOT revert `forward()` to a bare `void loadSentry().then(...)` with no
// `waitUntil` registration — that is this exact bug.

type SentryModule = typeof import('@sentry/nextjs')

let cached: SentryModule | null = null
let loading: Promise<SentryModule | null> | null = null

/**
 * True when Sentry must stay a no-op:
 *  - during `next build` / prerender (importing the SDK there is unsafe), or
 *  - when no DSN is set (nothing to report to; mirrors the SDK's own init guard).
 * Read lazily on each call so build-time and runtime evaluate independently.
 */
function sentryDisabled(): boolean {
  if (process.env.NEXT_PHASE === 'phase-production-build') return true
  if (!process.env.NEXT_PUBLIC_SENTRY_DSN && !process.env.SENTRY_DSN) return true
  return false
}

/**
 * Vercel's request-scoped `waitUntil`, when the platform exposes one, else
 * `null`. Reading this fresh on every call (rather than caching) matters
 * because the context is per-request — caching would leak one request's
 * `waitUntil` into a later invocation of a warm, reused instance.
 */
function getWaitUntil(): ((promise: Promise<unknown>) => void) | null {
  try {
    const ctx = (
      globalThis as unknown as Record<symbol, { get?: () => { waitUntil?: unknown } } | undefined>
    )[Symbol.for('@vercel/request-context')]
    const waitUntil = ctx?.get?.()?.waitUntil
    return typeof waitUntil === 'function'
      ? (waitUntil as (promise: Promise<unknown>) => void)
      : null
  } catch {
    return null
  }
}

/**
 * Register `task` with the platform's `waitUntil` when available, so a
 * fire-and-forget forward still gets to run before the function freezes. A
 * no-op when there is no such context (browser, local dev, non-Vercel host).
 */
function keepAlive(task: Promise<unknown>): void {
  const waitUntil = getWaitUntil()
  if (waitUntil) waitUntil(task)
}

/** Dynamically import `@sentry/nextjs` once, caching the module. Never rejects. */
function loadSentry(): Promise<SentryModule | null> {
  if (cached) return Promise.resolve(cached)
  if (loading) return loading
  loading = import('@sentry/nextjs')
    .then((mod) => {
      cached = mod
      return mod
    })
    .catch(() => null)
  return loading
}

/** Forward a fire-and-forget SDK call. Guarded and non-throwing by construction. */
function forward(method: keyof SentryModule, args: unknown[]): void {
  if (sentryDisabled()) return
  const task = loadSentry()
    .then((mod) => {
      if (!mod) return
      const fn = (mod as Record<string, unknown>)[method as string]
      if (typeof fn === 'function') {
        try {
          ;(fn as (...a: unknown[]) => unknown)(...args)
        } catch {
          // A capture failing must never break the caller.
        }
      }
    })
    .catch(() => {
      // Swallow any load/forward rejection — a Sentry outage is not an app error.
    })
  keepAlive(task)
}

/** A no-op scope so `withScope` callbacks that touch a scope never throw. */
function noopScope(): Record<string, () => void> {
  const noop = () => {}
  return new Proxy({}, { get: () => noop }) as Record<string, () => void>
}

const sentrySafe = {
  captureException: (...args: unknown[]): void => forward('captureException', args),
  captureMessage: (...args: unknown[]): void => forward('captureMessage', args),
  captureEvent: (...args: unknown[]): void => forward('captureEvent', args),
  addBreadcrumb: (...args: unknown[]): void => forward('addBreadcrumb', args),
  setTag: (...args: unknown[]): void => forward('setTag', args),
  setUser: (...args: unknown[]): void => forward('setUser', args),
  setContext: (...args: unknown[]): void => forward('setContext', args),
  // `withScope` takes a callback. When Sentry is live, forward to the real
  // implementation so the callback receives a real scope; otherwise invoke the
  // callback synchronously with a no-op scope (matches prior no-op behavior and
  // keeps any inline capture inside the callback flowing through this shim).
  withScope: (fn: (scope: unknown) => void): void => {
    if (sentryDisabled()) {
      try {
        fn(noopScope())
      } catch {
        /* callback must never throw into the caller */
      }
      return
    }
    const task = loadSentry()
      .then((mod) => {
        if (mod && typeof mod.withScope === 'function') {
          ;(mod.withScope as (cb: (scope: unknown) => void) => void)(fn)
        } else {
          try {
            fn(noopScope())
          } catch {
            /* no-op */
          }
        }
      })
      .catch(() => {})
    keepAlive(task)
  },
} as const

export default sentrySafe as typeof sentrySafe & Record<string, (...args: unknown[]) => void>
